import type { NodeObject } from 'react-force-graph-2d'
import type { ConceptGraph, GraphNode } from '../api/knowledgeApi'
import { forceX, forceY } from 'd3-force'
import { isQuery } from './graphModel'

export type LayoutMode = 'force' | 'categories' | 'hierarchy'
export const layoutDescriptions: Record<LayoutMode, string> = {
  force: 'Connections pull together; collision keeps cards apart. Drag a node to pin it.',
  categories:
    'Concepts gather by category. Groups are visual organisation, not asserted parent classes.',
  hierarchy:
    'Top to bottom: broader concepts, narrower concepts, then artworks. Only stored broader/variant assertions determine levels.',
}
export const nodeSize = (n: GraphNode) =>
  n.kind === 'artwork'
    ? { width: 112, height: 138 }
    : { width: isQuery(n) || n.centre ? 152 : 126, height: 44 }

/** Condense cycles before ranking. Opposites, associations and similarity are not parenthood. */
export function hierarchyRanks(graph: ConceptGraph) {
  const ids = graph.nodes.filter((n) => n.kind === 'concept').map((n) => n.id)
  const adjacent = new Map(ids.map((id) => [id, [] as string[]]))
  for (const e of graph.edges) {
    const parent = e.kind === 'broader_than' ? e.source : e.kind === 'variant_of' ? e.target : null
    const child = e.kind === 'broader_than' ? e.target : e.source
    if (parent && adjacent.has(parent) && adjacent.has(child)) adjacent.get(parent)!.push(child)
  }
  let clock = 0
  const index = new Map<string, number>(),
    low = new Map<string, number>()
  const stack: string[] = [],
    pending = new Set<string>(),
    component = new Map<string, number>()
  let count = 0
  function visit(id: string) {
    index.set(id, clock)
    low.set(id, clock++)
    stack.push(id)
    pending.add(id)
    for (const next of adjacent.get(id)!) {
      if (!index.has(next)) {
        visit(next)
        low.set(id, Math.min(low.get(id)!, low.get(next)!))
      } else if (pending.has(next)) low.set(id, Math.min(low.get(id)!, index.get(next)!))
    }
    if (low.get(id) === index.get(id)) {
      let next: string
      do {
        next = stack.pop()!
        pending.delete(next)
        component.set(next, count)
      } while (next !== id)
      count++
    }
  }
  ids.forEach((id) => {
    if (!index.has(id)) visit(id)
  })
  const outgoing = Array.from({ length: count }, () => new Set<number>())
  const indegree = Array(count).fill(0),
    rank = Array(count).fill(0)
  for (const [id, children] of adjacent)
    for (const child of children) {
      const a = component.get(id)!,
        b = component.get(child)!
      if (a !== b && !outgoing[a].has(b)) {
        outgoing[a].add(b)
        indegree[b]++
      }
    }
  const queue = indegree.flatMap((n, i) => (n === 0 ? [i] : []))
  for (let i = 0; i < queue.length; i++)
    for (const next of outgoing[queue[i]]) {
      rank[next] = Math.max(rank[next], rank[queue[i]] + 1)
      if (--indegree[next] === 0) queue.push(next)
    }
  const participating = new Set(
    graph.edges
      .filter((e) => ['broader_than', 'variant_of'].includes(e.kind))
      .flatMap((e) => [e.source, e.target])
  )
  return new Map(
    ids.filter((id) => participating.has(id)).map((id) => [id, rank[component.get(id)!]])
  )
}

export function layoutTargets(graph: ConceptGraph, mode: LayoutMode) {
  const ranks = hierarchyRanks(graph)
  const lastRank = Math.max(0, ...ranks.values())
  const targets = new Map<string, { x: number; y: number; group: string }>()
  const members = new Map<string, GraphNode[]>()
  for (const n of graph.nodes) {
    const group =
      mode === 'hierarchy'
        ? n.kind === 'artwork'
          ? 'Artworks'
          : ranks.has(n.id)
            ? `Level ${ranks.get(n.id)! + 1}`
            : 'Unranked · no hierarchy asserted'
        : n.kind === 'artwork'
          ? 'Artworks'
          : isQuery(n)
            ? 'Search query'
            : n.category || 'uncategorised'
    if (!members.has(group)) members.set(group, [])
    members.get(group)!.push(n)
  }
  // Pack group containers into wide shelves rather than forcing every category into a square cell.
  const cells = [...members]
    .map(([group, nodes]) => {
      const columns = Math.min(
        nodes.length,
        Math.max(1, Math.ceil(Math.sqrt(nodes.length * (group === 'Artworks' ? 3 : 1.8))))
      )
      return {
        group,
        columns,
        width: columns * 155 + 40,
        height: Math.ceil(nodes.length / columns) * (group === 'Artworks' ? 170 : 85) + 55,
      }
    })
    .sort((a, b) => b.height - a.height)
  const shelfWidth = Math.max(
    Math.sqrt(cells.reduce((n, c) => n + c.width * c.height, 0) * 2.7),
    (cells[0]?.width || 0) + (cells[1]?.width || 0) + 80
  )
  const packed = new Map<string, { x: number; y: number; columns: number }>()
  let x = 0,
    y = 0,
    rowHeight = 0
  for (const cell of cells) {
    if (x && x + cell.width > shelfWidth) {
      x = 0
      y += rowHeight + 36
      rowHeight = 0
    }
    packed.set(cell.group, {
      x: x + cell.width / 2 - shelfWidth / 2,
      y: y + cell.height / 2,
      columns: cell.columns,
    })
    x += cell.width + 36
    rowHeight = Math.max(rowHeight, cell.height)
  }
  const totalHeight = y + rowHeight
  for (const [group, nodes] of members)
    nodes.forEach((n, i) => {
      const cell = packed.get(group)!
      const gx = cell.x,
        gy = cell.y - totalHeight / 2
      const rowSize =
        mode === 'categories' ? cell.columns : Math.max(2, Math.ceil(Math.sqrt(nodes.length)))
      const dx = ((i % rowSize) - (Math.min(nodes.length, rowSize) - 1) / 2) * 155
      const dy =
        (Math.floor(i / rowSize) - (Math.ceil(nodes.length / rowSize) - 1) / 2) *
        (n.kind === 'artwork' ? 170 : 85)
      targets.set(
        n.id,
        mode === 'hierarchy'
          ? {
              x: dx + (ranks.has(n.id) || n.kind === 'artwork' ? 0 : 650),
              y: (n.kind === 'artwork' ? lastRank + 1 : ranks.get(n.id) || 0) * 270 + dy,
              group,
            }
          : mode === 'categories'
            ? { x: gx + dx, y: gy + dy, group }
            : {
                x: Math.cos(graph.nodes.indexOf(n) * 2.399) * (120 + graph.nodes.indexOf(n) * 10),
                y: Math.sin(graph.nodes.indexOf(n) * 2.399) * (120 + graph.nodes.indexOf(n) * 10),
                group,
              }
      )
    })
  return targets
}

export function layoutForces(graph: ConceptGraph, mode: LayoutMode) {
  const targets = layoutTargets(graph, mode)
  type Node = NodeObject<GraphNode>
  return {
    collision: cardCollision(),
    x: forceX<Node>((n) => (mode === 'force' ? 0 : targets.get(n.id)!.x)).strength(
      mode === 'force' ? 0.015 : 0.28
    ),
    y: forceY<Node>((n) => (mode === 'force' ? 0 : targets.get(n.id)!.y)).strength(
      mode === 'force' ? 0.18 : 0.4
    ),
  }
}

/** Rectangle collision includes the gallery mat and captions, rather than only the image. */
export function cardCollision() {
  type Node = NodeObject<GraphNode>
  let nodes: Node[] = []
  const force = () => {
    for (let pass = 0; pass < 3; pass++)
      for (let i = 0; i < nodes.length; i++)
        for (let j = i + 1; j < nodes.length; j++) {
          const a = nodes[i],
            b = nodes[j],
            sa = nodeSize(a),
            sb = nodeSize(b)
          const dx = (b.x || 0) + (b.vx || 0) - (a.x || 0) - (a.vx || 0) || (i % 2 ? 0.01 : -0.01)
          const dy = (b.y || 0) + (b.vy || 0) - (a.y || 0) - (a.vy || 0) || 0.01
          const ox = (sa.width + sb.width) / 2 + 14 - Math.abs(dx)
          const oy = (sa.height + sb.height) / 2 + 14 - Math.abs(dy)
          if (ox <= 0 || oy <= 0) continue
          if (ox < oy) {
            const push = Math.sign(dx) * ox * 0.5
            if (a.fx == null) a.vx = (a.vx || 0) - push
            if (b.fx == null) b.vx = (b.vx || 0) + push
          } else {
            const push = Math.sign(dy) * oy * 0.5
            if (a.fy == null) a.vy = (a.vy || 0) - push
            if (b.fy == null) b.vy = (b.vy || 0) + push
          }
        }
  }
  force.initialize = (next: Node[]) => {
    nodes = next
  }
  return force
}
